import { memo, useCallback } from 'react';

import { TriangleDown } from '@air/next-icons';
import { DropdownMenu } from '@air/primitive-dropdown-menu';
import { useSysadmin } from '@air/sysadmin';

import { WorkspaceHeaderInformationDropdownAccountItems } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownAccountItems';
import { WorkspaceHeaderInformationDropdownCreditUsage } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownCreditUsage';
import { WorkspaceHeaderInformationDropdownDevToolsItems } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownDevToolsItems';
import { WorkspaceHeaderInformationDropdownStorageItems } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownStorageItems';
import { WorkspaceHeaderInformationDropdownSwitchWorkspaceItems } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownSwitchWorkspaceItems';
import { WorkspaceHeaderInformationDropdownThemeItems } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownThemeItems';
import { WorkspaceHeaderInformationDropdownWorkspaceItems } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownWorkspaceItems';
import { useInternalMenu } from '~/components/v2/hooks/useInternalMenu';
import {
  useWorkspaceSidebarHoverSuppression,
  WORKSPACE_SIDEBAR_HEADER_DROPDOWN_TOKEN,
} from '~/components/v2/hooks/useWorkspaceSidebarHoverSuppression';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

const WORKSPACE_HEADER_DROPDOWN_LOCK_TOKEN = 'workspace-header-dropdown-lock';

export const WorkspaceHeaderInformationDropdown = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { isInternal } = useSysadmin();
  const { isInternalMenuVisible } = useInternalMenu();
  const { handleMenuOpenChange } = useWorkspaceSidebarHoverSuppression({
    token: WORKSPACE_SIDEBAR_HEADER_DROPDOWN_TOKEN,
  });
  const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
    token: WORKSPACE_HEADER_DROPDOWN_LOCK_TOKEN,
  });

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      handleMenuOpenChange(open);
      handleLockOpenChange(open);
    },
    [handleMenuOpenChange, handleLockOpenChange],
  );

  return (
    <DropdownMenu
      className="w-[calc(var(--sidebar-width)-24px)] min-w-[269px]"
      alignOffset={-48}
      onOpenChange={handleDropdownOpenChange}
      trigger={
        currentWorkspace ? (
          <button
            className="flex h-8 min-w-0 items-center justify-center gap-1 rounded-8 px-2 font-semibold text-16 text-grey-11 outline-none transition-colors hover:bg-grey-5 focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset active:bg-grey-6 data-[state=open]:bg-grey-5"
            data-testid="ACCOUNT_POPOVER_BUTTON"
            type="button"
          >
            <span className="min-w-0 truncate">{currentWorkspace.name}</span>
            <TriangleDown className="size-4 shrink-0" />
          </button>
        ) : null
      }
    >
      <WorkspaceHeaderInformationDropdownCreditUsage />
      <WorkspaceHeaderInformationDropdownStorageItems />
      <WorkspaceHeaderInformationDropdownWorkspaceItems />
      {!isInternal && <WorkspaceHeaderInformationDropdownSwitchWorkspaceItems />}
      <WorkspaceHeaderInformationDropdownThemeItems />
      {isInternalMenuVisible && <WorkspaceHeaderInformationDropdownDevToolsItems />}
      <WorkspaceHeaderInformationDropdownAccountItems />
    </DropdownMenu>
  );
});

WorkspaceHeaderInformationDropdown.displayName = 'WorkspaceHeaderInformationDropdown';
